import {
    faChevronDown,
    faChevronUp,
    faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
    selectCarriedForwardDataV2,
    selectPositionUnitMapV2,
    selectPositionsV2,
} from "../../../../../store/slices/aufmassV2Slice";
import {
    formatQuantityToLocale,
    getFormattedDate,
} from "../../../../../utils/method";
import GroupItemV2 from "./GroupItemV2";
import LineItemV2 from "./LineItemV2";
import './CarriedForwardSectionV2.scss';

function CarriedForwardSectionV2({ position }) {
    const { t } = useTranslation();

    const [isExpanded, setIsExpanded] = useState(false);
    const positionId = position.id;

    const carriedForwardData = useSelector(selectCarriedForwardDataV2);
    const positionUnitMap = useSelector(selectPositionUnitMapV2);
    const positions = useSelector(selectPositionsV2);

    const toggleExpanded = () => { setIsExpanded(!isExpanded); };

    const groups = useMemo(() => {
        const positionData = positions[positionId];
        const invoicePositionId = positionData?.invoicePositionId;
        return invoicePositionId ? (carriedForwardData[invoicePositionId] || []) : [];
    }, [carriedForwardData, positionId, positions]);

    const renderCarriedForwardBadge = useCallback(
        (item) => {
            if (!item?.isCarriedForward) return null;

            const sourceInfo = item.sourceInfo || {};
            let tooltipContent = t("aufmass.carried-forward-tooltip");

            if (sourceInfo.invoiceNumber) {
                tooltipContent += ` ${t("aufmass.from-invoice")}: ${sourceInfo.invoiceNumber}`;
                if (sourceInfo.invoiceDate) {
                    tooltipContent += ` (${getFormattedDate(sourceInfo.invoiceDate)})`;
                }
                if (sourceInfo.aufmassName) {
                    tooltipContent += ` - ${sourceInfo.aufmassName}`;
                }
            }

            return (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{tooltipContent}</Tooltip>}
                >
                    <span className="carried-forward-section__badge">
                        <FontAwesomeIcon icon={faInfoCircle} size="xs" />
                        {t("aufmass.carried-forward")}
                    </span>
                </OverlayTrigger>
            );
        },
        [t],
    );

    if (!groups.length) return null;

    const totalQuantity = groups.reduce((sum, group) => {
        return sum + (group.lines || []).reduce((ls, line) => ls + parseFloat(line.quantity || 0), 0);
    }, 0);

    return (
        <div className="carried-forward-section">
            <div
                className={`carried-forward-section__header${isExpanded ? " carried-forward-section__header--expanded" : ""}`}
                onClick={toggleExpanded}
            >
                <div className="carried-forward-section__header-left">
                    <FontAwesomeIcon
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        className="carried-forward-section__chevron"
                    />
                    <span className="carried-forward-section__label">
                        {t("aufmass.carried-forward-measurements")}
                    </span>
                    <span className="carried-forward-section__count-badge">
                        {groups.length}{" "}
                        {groups.length === 1 ? t("aufmass.group") : t("aufmass.group-plural")}
                    </span>
                </div>
                <div className="carried-forward-section__total">
                    {t("aufmass.total")}:{" "}
                    {formatQuantityToLocale(totalQuantity)}{" "}
                    {positionUnitMap[positionId] || position.unit}
                </div>
            </div>

            {isExpanded && (
                <div className="carried-forward-section__content">
                    {groups.map((group, groupIndex) => (
                        <div key={groupIndex} className="carried-forward-section__group-item">
                            <GroupItemV2.Header
                                group={group}
                                groupIndex={groupIndex}
                                position={position}
                                isCarriedForward={true}
                                renderCarriedForwardBadge={renderCarriedForwardBadge}
                            />
                            {group.lines && group.lines.map((line, lineIndex) => (
                                <LineItemV2
                                    key={lineIndex}
                                    line={line}
                                    lineIndex={lineIndex}
                                    groupIndex={groupIndex}
                                    position={position}
                                    isCarriedForward={true}
                                    renderCarriedForwardBadge={renderCarriedForwardBadge}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default CarriedForwardSectionV2;
